// The domain magic link emails are sent from. Used to build provider search URLs
// so the user lands directly on the right email rather than their general inbox.
const MAGIC_LINK_SENDER_DOMAIN = 'swonkie.com';

type InboxProvider = {
    name: string;
    /** Plain inbox URL — used for providers without search deep-link support */
    inboxUrl: string;
    /**
     * Builds a search-scoped URL for this provider.
     * Returns null for providers that don't support search deep-links.
     */
    searchUrl: ((query: string) => string) | null;
    i18nKey: string;
};

const INBOX_PROVIDERS: Record<string, InboxProvider> = {
    // Google — #search/<query> works without a /u/N/ user index; provider redirects to active account
    'gmail.com': {
        name: 'Gmail',
        inboxUrl: 'https://mail.google.com/mail/',
        searchUrl: (q) => `https://mail.google.com/mail/#search/${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.gmail',
    },
    'googlemail.com': {
        name: 'Gmail',
        inboxUrl: 'https://mail.google.com/mail/',
        searchUrl: (q) => `https://mail.google.com/mail/#search/${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.gmail',
    },
    // Microsoft — /mail/search?q= works without a user-index segment
    'outlook.com': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'hotmail.com': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'hotmail.co.uk': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'hotmail.fr': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'live.com': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'live.co.uk': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    'msn.com': {
        name: 'Outlook',
        inboxUrl: 'https://outlook.live.com/mail/',
        searchUrl: (q) => `https://outlook.live.com/mail/search?q=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.outlook',
    },
    // Yahoo — /d/search/keyword= has no user-index
    'yahoo.com': {
        name: 'Yahoo Mail',
        inboxUrl: 'https://mail.yahoo.com/',
        searchUrl: (q) => `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.yahoo',
    },
    'yahoo.co.uk': {
        name: 'Yahoo Mail',
        inboxUrl: 'https://mail.yahoo.com/',
        searchUrl: (q) => `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.yahoo',
    },
    'yahoo.fr': {
        name: 'Yahoo Mail',
        inboxUrl: 'https://mail.yahoo.com/',
        searchUrl: (q) => `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.yahoo',
    },
    'yahoo.es': {
        name: 'Yahoo Mail',
        inboxUrl: 'https://mail.yahoo.com/',
        searchUrl: (q) => `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.yahoo',
    },
    'yahoo.com.br': {
        name: 'Yahoo Mail',
        inboxUrl: 'https://mail.yahoo.com/',
        searchUrl: (q) => `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.yahoo',
    },
    // Apple iCloud — no public search deep-link; fall back to plain inbox
    'icloud.com': {
        name: 'iCloud Mail',
        inboxUrl: 'https://www.icloud.com/mail/',
        searchUrl: null,
        i18nKey: 'auth.inbox-provider.icloud',
    },
    'me.com': {
        name: 'iCloud Mail',
        inboxUrl: 'https://www.icloud.com/mail/',
        searchUrl: null,
        i18nKey: 'auth.inbox-provider.icloud',
    },
    'mac.com': {
        name: 'iCloud Mail',
        inboxUrl: 'https://www.icloud.com/mail/',
        searchUrl: null,
        i18nKey: 'auth.inbox-provider.icloud',
    },
    // Proton — /search?keyword= has no user-index
    'protonmail.com': {
        name: 'Proton Mail',
        inboxUrl: 'https://mail.proton.me/',
        searchUrl: (q) => `https://mail.proton.me/search?keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.proton',
    },
    'proton.me': {
        name: 'Proton Mail',
        inboxUrl: 'https://mail.proton.me/',
        searchUrl: (q) => `https://mail.proton.me/search?keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.proton',
    },
    // AOL — same search URL scheme as Yahoo (shared codebase)
    'aol.com': {
        name: 'AOL Mail',
        inboxUrl: 'https://mail.aol.com/',
        searchUrl: (q) => `https://mail.aol.com/d/search/keyword=${encodeURIComponent(q)}`,
        i18nKey: 'auth.inbox-provider.aol',
    },
    // Zoho — no reliable search deep-link; fall back to plain inbox
    'zoho.com': {
        name: 'Zoho Mail',
        inboxUrl: 'https://mail.zoho.com/',
        searchUrl: null,
        i18nKey: 'auth.inbox-provider.zoho',
    },
    'zohomail.com': {
        name: 'Zoho Mail',
        inboxUrl: 'https://mail.zoho.com/',
        searchUrl: null,
        i18nKey: 'auth.inbox-provider.zoho',
    },
};

export type EmailInboxInfo = {
    name: string;
    url: string;
    i18nKey: string;
};

/**
 * Returns inbox info for known webmail providers, or null if the domain is unknown
 * (e.g. a custom business domain).
 *
 * For providers that support search deep-links, the URL is pre-scoped to
 * `from:swonkie.com` so the user lands directly on the magic link email.
 * For providers without search support (iCloud, Zoho), the plain inbox URL is used.
 */
export function getEmailInboxInfo(email: string): EmailInboxInfo | null {
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) return null;
    const domain = email
        .slice(atIndex + 1)
        .toLowerCase()
        .trim();
    const provider = INBOX_PROVIDERS[domain];
    if (!provider) return null;

    const searchQuery = `from:${MAGIC_LINK_SENDER_DOMAIN}`;
    const url = provider.searchUrl ? provider.searchUrl(searchQuery) : provider.inboxUrl;

    return { name: provider.name, url, i18nKey: provider.i18nKey };
}
